import { ChatMessage, ChatThread } from '../../contexts/ChatContext';
import { EnhancedArtifact } from '../../types/artifacts';
import { ModelType } from '../../types/models';

export interface SampleChatService {
  getSampleThreads: () => Promise<ChatThread[]>;
  getSampleMessages: (threadId: string, artifacts: EnhancedArtifact[]) => Promise<ChatMessage[]>;
}

class SampleChatServiceImpl implements SampleChatService {
  async getSampleThreads(): Promise<ChatThread[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));

    return [
      {
        id: 'sample-overview',
        name: 'Sample Data Overview',
        description: 'Overview of available sample artifacts',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messageCount: 1,
        isPinned: true,
        tags: ['sample', 'artifacts', 'overview'],
        participants: [],
        model: { type: ModelType.COMPANY },
        uiRole: 'architect',
      },
      {
        id: 'thread-financial-analysis',
        name: 'Financial Analysis Discussion',
        description: 'Discussion about financial reporting and analysis processes',
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
        updatedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
        messageCount: 5,
        isPinned: true,
        tags: ['finance', 'analysis', 'reporting'],
        participants: [],
        model: { type: ModelType.COMPANY },
        uiRole: 'operator',
      },
      {
        id: 'thread-customer-improvement',
        name: 'Customer Experience Improvement',
        description: 'Working on enhancing customer service processes',
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4 hours ago
        updatedAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 minutes ago
        messageCount: 8,
        isPinned: false,
        tags: ['customer', 'improvement', 'service'],
        participants: [],
        model: { type: ModelType.COMPANY },
        uiRole: 'coach',
      }
    ];
  }

  async getSampleMessages(threadId: string, artifacts: EnhancedArtifact[]): Promise<ChatMessage[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));

    const baseMessages: ChatMessage[] = [];

    switch (threadId) {
      case 'sample-overview':
        baseMessages.push({
          id: 'overview-welcome',
          threadId,
          who: 'assistant',
          text: 'Welcome to the sample data mode! Here you can explore the available artifacts and their relationships. This demo includes various types of business artifacts including entities, processes, and solutions.',
          timestamp: new Date().toISOString(),
          type: 'text',
        });

        // Add artifact cards for overview
        if (artifacts.length > 0) {
          baseMessages.push({
            id: 'overview-artifacts',
            threadId,
            who: 'assistant',
            text: `Here are the ${artifacts.length} sample artifacts available:`,
            timestamp: new Date(Date.now() + 1000).toISOString(),
            type: 'card',
            metadata: {
              cardType: 'artifact-overview',
              artifactId: artifacts[0].id, // Use first artifact ID for now
            },
          });
        }
        break;

      case 'thread-financial-analysis': {
        baseMessages.push(
          {
            id: 'financial-welcome',
            threadId,
            who: 'user',
            text: 'I need to analyze our Q4 financial performance. Can you help me understand the key metrics?',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            type: 'text',
          },
          {
            id: 'financial-response',
            threadId,
            who: 'assistant',
            text: 'I\'d be happy to help with your Q4 financial analysis. Let me pull up the relevant financial artifacts and reports.',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 + 30000).toISOString(),
            type: 'text',
          },
          {
            id: 'financial-followup',
            threadId,
            who: 'user',
            text: 'What about the operating checking account? I noticed some discrepancies in the reconciliation.',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
            type: 'text',
          },
          {
            id: 'financial-operating-checking',
            threadId,
            who: 'assistant',
            text: 'Let me examine the Operating Checking account details for you.',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000 + 15000).toISOString(),
            type: 'card',
            metadata: {
              cardType: 'artifact-detail',
              artifactId: artifacts.find(a => a.name === 'Operating Checking')?.id || artifacts[0]?.id,
            },
          },
          {
            id: 'financial-summary',
            threadId,
            who: 'assistant',
            text: 'I found the reconciliation issues. The main discrepancies are in the automated transfer processes. Would you like me to create a detailed report?',
            timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
            type: 'text',
          }
        );
        break;
      }

      case 'thread-customer-improvement': {
        baseMessages.push(
          {
            id: 'customer-welcome',
            threadId,
            who: 'user',
            text: 'Our customer satisfaction scores have dropped this quarter. We need to identify improvement opportunities.',
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
            type: 'text',
          },
          {
            id: 'customer-analysis',
            threadId,
            who: 'assistant',
            text: 'I can help analyze the customer experience data and identify improvement areas. Let me examine the current processes.',
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000 + 45000).toISOString(),
            type: 'text',
          },
          {
            id: 'customer-processes',
            threadId,
            who: 'user',
            text: 'Start with the customer onboarding process. That seems to be where most complaints originate.',
            timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
            type: 'text',
          },
          {
            id: 'customer-process-analysis',
            threadId,
            who: 'assistant',
            text: 'Analyzing the customer onboarding process... Here are the key areas that need attention:',
            timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000 + 20000).toISOString(),
            type: 'text',
          },
          {
            id: 'customer-findings',
            threadId,
            who: 'assistant',
            text: 'I\'ve identified 3 main bottlenecks: 1) Documentation requirements are too complex, 2) Approval workflow has unnecessary delays, 3) Communication gaps between departments.',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            type: 'text',
          },
          {
            id: 'customer-solutions',
            threadId,
            who: 'user',
            text: 'Can you propose specific solutions for each of these issues?',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
            type: 'text',
          },
          {
            id: 'customer-proposals',
            threadId,
            who: 'assistant',
            text: 'I recommend: 1) Simplify documentation to essential items only, 2) Implement automated approval for standard cases, 3) Create a shared customer dashboard for real-time updates.',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000 + 30000).toISOString(),
            type: 'text',
          },
          {
            id: 'customer-next-steps',
            threadId,
            who: 'user',
            text: 'Great suggestions! Let\'s prioritize the shared dashboard first - that seems like the quickest win.',
            timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
            type: 'text',
          }
        );
        break;
      }

      default:
        baseMessages.push({
          id: 'default-welcome',
          threadId,
          who: 'assistant',
          text: 'Welcome to the sample data mode! This thread contains sample artifacts for demonstration purposes.',
          timestamp: new Date().toISOString(),
          type: 'text',
        });
    }

    return baseMessages;
  }
}

// Export singleton instance
export const sampleChatService = new SampleChatServiceImpl();
